import { TFunction } from 'i18next'
import { z } from 'zod'

import { ZIP_CODE_CA_REGEX, ZIP_CODE_US_REGEX } from '@/constants/regex'
import { UserRole, UserRoleEnum } from '@/models/features/user.models'
import { isProductionBuild } from '@/utils/environment'
import { getPhoneEntryMaxDigits } from '@/utils/phone-number'

import { normalizeZipCode } from './general-information.utils'

export const ProfileBeFieldEnum = z.enum([
  'apartment',
  'avatar',
  'birth',
  'buildingNumber',
  'email',
  'firstName',
  'floor',
  'inHouse',
  'lastName',
  'leaseEnd',
  'leaseStart',
  'mailingList',
  'moveInDate',
  'moveOutDate',
  'pets',
  'phone',
  'pmsExternalId',
  'street',
  'tags',
  'zipCode',
])

export type ProfileBeField = z.infer<typeof ProfileBeFieldEnum>

export const ALWAYS_VISIBLE_MASK_OPTIONS = {
  clearMaskOnLostFocus: false,
  placeholder: '*',
  showMaskOnFocus: false,
  showMaskOnHover: false,
}

export const getPmsTooltip = (t: TFunction<['clients']>): string =>
  t('clients:generalInformation.tooltips.pms')
export const getPmsEditTooltip = (t: TFunction<['clients']>): string =>
  t('clients:generalInformation.tooltips.pmsEdit')
export const getPhonePmsTooltip = (t: TFunction<['clients']>): string =>
  t('clients:generalInformation.tooltips.phonePms')

export const PROFILE_PHOTO_ACCEPTED_TYPES = ['.jpeg', '.jpg', '.png']

// Fixed, application-wide mailing list ids (see backend MailingListService.getAll) — clients
// can never be unsubscribed from these, so their chips must not be removable in the UI.
export const PROTECTED_MAILING_LIST_IDS = ['9', '10']

// 10 digits for the base number, optionally followed by a 1-4 digit extension (0-9999)
// appended at the end (see formatPhoneNumberInput) — 11-13 digits means a partial extension.
// getPhoneEntryMaxDigits caps this at exactly 10 (no extension) in production, matching
// Utilities.validatePhoneNumber there — the _production i18next context picks the matching
// error copy (see clients.json's invalid_production key) instead of still saying "10-14".
const makePhoneSchema = (t: TFunction<['clients']>) =>
  z.string().refine(
    (val) => {
      if (!val) return true
      const digits = val.replace(/\D/g, '')
      return (
        digits.length === 10 || (digits.length > 10 && digits.length <= getPhoneEntryMaxDigits())
      )
    },
    {
      message: t('clients:generalInformation.phoneNumber.validation.invalid', {
        context: isProductionBuild ? 'production' : undefined,
      }),
    },
  )

export const isFieldVisible = (beField: ProfileBeField | null, hiddenFields: string[]): boolean =>
  !beField || !hiddenFields.includes(beField)

export const isFieldSynced = (beField: ProfileBeField | null, syncedFields: string[]): boolean =>
  Boolean(beField && syncedFields.includes(beField))

const GENERAL_INFO_EDIT_ALLOWED_ROLES: UserRole[] = [
  UserRoleEnum.enum.Administrator,
  UserRoleEnum.enum.Client,
  UserRoleEnum.enum.Manager,
  UserRoleEnum.enum.PropertyManager,
  UserRoleEnum.enum.ShiftManager,
  UserRoleEnum.enum.Staff,
  UserRoleEnum.enum.Valet,
]

export const canEditGeneralInfo = (userRole: string | null): boolean =>
  !!userRole && (GENERAL_INFO_EDIT_ALLOWED_ROLES as string[]).includes(userRole)

const BUILDING_CHANGE_ALLOWED_ROLES: UserRole[] = [
  UserRoleEnum.enum.Administrator,
  UserRoleEnum.enum.Manager,
]

export const canChangeBuilding = (userRole: string | null): boolean =>
  !!userRole && (BUILDING_CHANGE_ALLOWED_ROLES as string[]).includes(userRole)

export const composeUpdateFormData = (
  values: z.infer<ReturnType<typeof makeGeneralInfoSchema>>,
  syncedFields: string[],
  isFloorDirty: boolean,
): FormData => {
  const formData = new FormData()

  const appendIfNotSynced = (beField: string, value: string) => {
    if (!syncedFields.includes(beField)) formData.append(beField, value)
  }

  appendIfNotSynced(ProfileBeFieldEnum.enum.firstName, values.firstName)
  appendIfNotSynced(ProfileBeFieldEnum.enum.lastName, values.lastName)
  const phoneDigits = values.phoneNumber.replace(/\D/g, '')
  // Note: BE's phone validator (Utilities.validatePhoneNumber) requires exactly 10 digits in
  // production but 11-14 (a leading country-code digit) everywhere else — see getPhoneMaxLength
  // in Clients/helpers.jsx for the same split on the legacy profile form. Padding a bare
  // 10-digit number unconditionally broke saving a valid production phone number.
  const phoneToSend =
    !isProductionBuild && phoneDigits.length === 10 ? `1${phoneDigits}` : phoneDigits
  appendIfNotSynced(ProfileBeFieldEnum.enum.phone, phoneToSend || '')
  appendIfNotSynced(ProfileBeFieldEnum.enum.street, values.address)
  appendIfNotSynced(ProfileBeFieldEnum.enum.zipCode, values.zipCode)
  appendIfNotSynced(ProfileBeFieldEnum.enum.apartment, values.unit)
  if (isFloorDirty) appendIfNotSynced(ProfileBeFieldEnum.enum.floor, values.floor)
  appendIfNotSynced(ProfileBeFieldEnum.enum.pets, String(values.pets))
  appendIfNotSynced(ProfileBeFieldEnum.enum.inHouse, String(values.inHouseDelivery))
  formData.append('buildingId', String(values.buildingId))
  const appendDateIfNotSynced = (beField: string, value: Date | null) => {
    if (!syncedFields.includes(beField)) {
      formData.append(beField, value ? String(value.getTime()) : 'null')
    }
  }

  appendDateIfNotSynced(ProfileBeFieldEnum.enum.moveInDate, values.moveInDate)
  appendDateIfNotSynced(ProfileBeFieldEnum.enum.moveOutDate, values.moveOutDate)
  appendDateIfNotSynced(ProfileBeFieldEnum.enum.leaseStart, values.leaseStartDate)
  appendDateIfNotSynced(ProfileBeFieldEnum.enum.leaseEnd, values.leaseEndDate)
  appendDateIfNotSynced(ProfileBeFieldEnum.enum.birth, values.dateOfBirth)

  if (values.photo) {
    formData.append(ProfileBeFieldEnum.enum.avatar, values.photo)
  } else if (values.removeExistingPhoto) {
    formData.append('removeAvatar', 'true')
  }

  values.tags.forEach((tag) => formData.append(ProfileBeFieldEnum.enum.tags, tag))

  return formData
}

export const GENERAL_INFO_GROUPS = ['personalAndContact', 'residence', 'lease', 'other'] as const

export type GeneralInfoGroup = (typeof GENERAL_INFO_GROUPS)[number]

// Note: groups with no entry are always shown; the others are hidden once every one of their rows is
const GENERAL_INFO_GROUP_FIELDS: Partial<Record<GeneralInfoGroup, readonly ProfileBeField[]>> = {
  lease: [
    ProfileBeFieldEnum.enum.moveInDate,
    ProfileBeFieldEnum.enum.moveOutDate,
    ProfileBeFieldEnum.enum.leaseStart,
    ProfileBeFieldEnum.enum.leaseEnd,
  ],
  residence: [
    ProfileBeFieldEnum.enum.street,
    ProfileBeFieldEnum.enum.zipCode,
    ProfileBeFieldEnum.enum.buildingNumber,
    ProfileBeFieldEnum.enum.apartment,
    ProfileBeFieldEnum.enum.floor,
  ],
}

export const isGeneralInfoGroupVisible = (
  group: GeneralInfoGroup,
  hiddenFields: string[],
  userRole: string | null,
): boolean => {
  const fields = GENERAL_INFO_GROUP_FIELDS[group]
  if (!fields) return true
  // Note: the building row is not a profile field, it only depends on the role
  if (group === 'residence' && canChangeBuilding(userRole)) return true
  return fields.some((field) => isFieldVisible(field, hiddenFields))
}

export const SKELETON_GROUPS = [
  {
    fields: ['firstName', 'lastName', 'email', 'phoneNumber', 'dateOfBirth'],
    group: 'personalAndContact',
  },
  { fields: ['address', 'zipCode', 'buildingNumber', 'unit', 'floor'], group: 'residence' },
  {
    fields: ['moveInDate', 'moveOutDate', 'leaseStartDate', 'leaseEndDate'],
    group: 'lease',
  },
  { fields: ['pets', 'tags', 'mailingList'], group: 'other' },
] as const satisfies ReadonlyArray<{ fields: readonly string[]; group: GeneralInfoGroup }>

export const MAX_VISIBLE_CHIPS = 10

export const GENERAL_INFO_FIELD_LABELS: Record<string, string> = {
  address: 'Address',
  buildingId: 'Building',
  dateOfBirth: 'Date of birth',
  firstName: 'First name',
  floor: 'Floor',
  inHouseDelivery: 'In-house delivery',
  lastName: 'Last name',
  leaseEndDate: 'Lease end date',
  leaseStartDate: 'Lease start date',
  mailingList: 'Mailing list',
  moveInDate: 'Move-in date',
  moveOutDate: 'Move-out date',
  pets: 'Pets',
  phoneNumber: 'Phone number',
  photo: 'Profile photo',
  tags: 'Tags',
  unit: 'Unit',
  zipCode: 'Zip code',
}

// PMS-synced fields are read-only in the UI (see EditableTextRow's `isSynchronized` branch) —
// their value is whatever the PMS last sent, not something the current user typed. Validating
// them against these format rules blocks saving unrelated, actually-editable fields whenever a
// PMS resident has legacy invalid/incomplete data in a synced field the user can't fix.
export const makeGeneralInfoSchema = (t: TFunction<['clients']>, syncedFields: string[] = []) => {
  const isSynced = (field: ProfileBeField) => syncedFields.includes(field)

  return z.object({
    address: isSynced(ProfileBeFieldEnum.enum.street)
      ? z.string()
      : z
          .string()
          .min(1, t('clients:generalInformation.address.validation.required'))
          .max(255, t('clients:generalInformation.address.validation.maxLength')),
    buildingId: z.number(),
    dateOfBirth: z.date().nullable(),
    firstName: isSynced(ProfileBeFieldEnum.enum.firstName)
      ? z.string()
      : z
          .string()
          .min(1, t('clients:generalInformation.firstName.validation.required'))
          .max(255, t('clients:generalInformation.firstName.validation.maxLength')),
    floor: z.string(),
    inHouseDelivery: z.boolean(),
    lastName: isSynced(ProfileBeFieldEnum.enum.lastName)
      ? z.string()
      : z
          .string()
          .min(1, t('clients:generalInformation.lastName.validation.required'))
          .max(255, t('clients:generalInformation.lastName.validation.maxLength')),
    leaseEndDate: z.date().nullable(),
    leaseStartDate: z.date().nullable(),
    mailingList: z
      .array(z.string())
      .max(50, t('clients:generalInformation.mailingList.validation.maxLength')),
    moveInDate: z.date().nullable(),
    moveOutDate: z.date().nullable(),
    pets: z.boolean(),
    phoneNumber: isSynced(ProfileBeFieldEnum.enum.phone) ? z.string() : makePhoneSchema(t),
    photo: z.instanceof(File).nullable(),
    removeExistingPhoto: z.boolean(),
    tags: z.array(z.string()).max(50, t('clients:generalInformation.tags.validation.maxLength')),
    unit: isSynced(ProfileBeFieldEnum.enum.apartment)
      ? z.string()
      : z
          .string()
          .min(1, t('clients:generalInformation.unit.validation.required'))
          .max(255, t('clients:generalInformation.unit.validation.maxLength')),
    zipCode: isSynced(ProfileBeFieldEnum.enum.zipCode)
      ? z.string()
      : z
          .string()
          .min(1, t('clients:generalInformation.zipCode.validation.required'))
          .refine((val) => ZIP_CODE_US_REGEX.test(val) || ZIP_CODE_CA_REGEX.test(val), {
            message: t('clients:generalInformation.zipCode.validation.invalid'),
          })
          .transform((val) => normalizeZipCode(val)),
  })
}
